function __safeCall<T = any>(callback: CallableFunction): T {
	try {
		if (typeof window !== 'undefined') {
			return callback();
		}
	} catch {
		// @note Silently fail if window is undefined.
	}

	return undefined as unknown as T;
}

let currentIndex: number = 0;
let previousPath: string | null = null;

export type Blocker = (path: string, proceed: () => void) => boolean;

let blocker: Blocker | null = null;
let ignoreNextPop: boolean = false;
let bypassNextPop: boolean = false;
let popTransition: NavigationTransition | undefined;
/** Set while this module writes the address, so its own writes are told apart. */
let isWriting: boolean = false;

export function block(next: Blocker): () => void {
	blocker = next;

	return () => {
		if (blocker === next) {
			blocker = null;
		}
	};
}

type Callback = (
	data: any,
	unused: string,
	url?: string | URL | null | undefined
) => void;

// eslint-disable-next-line teslint/no-explicit-any
type Mutator = (state: any, ...args: any[]) => void;

export type NavigationTransition = 'default' | 'slide-in' | 'slide-out';

export interface NavigationOptions {
	emitEvents?: boolean;
	transition?: NavigationTransition;
}

interface LocationChangeEventPayload {
	direction: string;
	transition?: NavigationTransition;
}

export const LocationChangeEvent = 'location:change';
export const LocationPushEvent = 'location:push';
export const LocationReplaceEvent = 'location:replace';
export const LocationForwardEvent = 'location:forward';
export const LocationBackwardEvent = 'location:backward';
export const LocationRefreshEvent = 'location:refresh';

/**
 *	Fired, once `init()` has run, whenever something other than this module
 *	writes an address to the browser's history with `pushState` or
 *	`replaceState`, such as the legacy app's own router. Fired synchronously,
 *	after the write, so `window.location` is already the new address. This
 *	module's own `push` and `replace` announce themselves with
 *	`LocationChangeEvent` instead, and do not fire it.
 */
export const LocationWriteEvent = 'location:write';

export function init(): void {
	__safeCall(() => {
		// @NOTE Set inital state
		if (!window.history.state || !('index' in window.history.state)) {
			currentIndex = (window.history.state && window.history.state.index) ?? 0;

			window.history.replaceState(
				{
					index: currentIndex,
					state: window.history.state,
				},
				document.title
			);
		}

		const getState = Object.getOwnPropertyDescriptor(
			History.prototype,
			'state'
		)!.get;

		const { pushState, replaceState } = window.history;

		function observePopState(): void {
			const state = getState!.call(window.history);

			if (ignoreNextPop) {
				ignoreNextPop = false;
				return;
			}

			if (bypassNextPop) {
				bypassNextPop = false;
			} else if (state && blocker) {
				const delta: number = state.index - currentIndex;

				const proceed = () => {
					bypassNextPop = true;
					window.history.go(delta);
				};

				if (blocker(window.location.pathname, proceed)) {
					// @NOTE popstate can't be cancelled, so step back to where we were.
					ignoreNextPop = true;
					window.history.go(-delta);
					return;
				}
			}

			// @NOTE Update incremented index whenever `location.hash` is set as it unsets state
			if (!state) {
				replaceState.call(
					window.history,
					{ index: currentIndex + 1 },
					document.title
				);
			}

			const index: number = state ? state.index : currentIndex + 1;

			const direction =
				index > currentIndex ? LocationForwardEvent : LocationBackwardEvent;

			const transition = popTransition;

			popTransition = undefined;

			window.dispatchEvent(new Event(direction));
			window.dispatchEvent(
				new CustomEvent<LocationChangeEventPayload>(LocationChangeEvent, {
					detail: { direction, transition },
				})
			);

			currentIndex = index;
		}

		function stateMutator(callback: Callback, index: number): Mutator {
			return (state, unused: string, url?: string | URL | null | undefined) => {
				// Marked as writing while it writes, so a mutator it wraps, when
				// `init()` has run more than once, does not announce it again.
				const isOwnWrite = isWriting;

				isWriting = true;

				try {
					callback.call(
						window.history,
						{ index: currentIndex + index, state },
						unused,
						url
					);
				} finally {
					isWriting = isOwnWrite;
				}

				currentIndex += index;

				if (!isOwnWrite && url !== undefined && url !== null) {
					window.dispatchEvent(new Event(LocationWriteEvent));
				}
			};
		}

		function stateAccessor(
			$class: typeof History | typeof PopStateEvent
		): void {
			const { get } = Object.getOwnPropertyDescriptor(
				$class.prototype,
				'state'
			)!;

			Object.defineProperty($class.prototype, 'state', {
				configurable: true,
				enumerable: true,
				get() {
					return get!.call(this).state;
				},
				set: undefined,
			});
		}

		stateAccessor(History);
		stateAccessor(PopStateEvent);

		window.history.pushState = stateMutator(pushState, 1);
		window.history.replaceState = stateMutator(replaceState, 0);

		window.addEventListener('popstate', observePopState);
	});
}

/** Writes the address as this module, without firing `LocationWriteEvent`. */
function writeOwnAddress(write: () => void): void {
	isWriting = true;

	try {
		write();
	} finally {
		isWriting = false;
	}
}

export function push(path: string, options: NavigationOptions = {}): void {
	if (blocker?.(path, () => pushUnblocked(path, options))) {
		return;
	}

	pushUnblocked(path, options);
}

function pushUnblocked(
	path: string,
	{ emitEvents = true, transition }: NavigationOptions
): void {
	__safeCall(() => {
		previousPath = window.location.pathname;
		writeOwnAddress(() => window.history.pushState(null, '', path));
		window.scrollTo({ top: 0 });

		if (emitEvents) {
			window.dispatchEvent(new Event(LocationPushEvent));
			window.dispatchEvent(
				new CustomEvent<LocationChangeEventPayload>(LocationChangeEvent, {
					detail: { direction: LocationPushEvent, transition },
				})
			);
		}
	});
}

export function replace(path: string, options: NavigationOptions = {}): void {
	if (blocker?.(path, () => replaceUnblocked(path, options))) {
		return;
	}

	replaceUnblocked(path, options);
}

function replaceUnblocked(
	path: string,
	{ emitEvents = true, transition }: NavigationOptions
): void {
	__safeCall(() => {
		writeOwnAddress(() => window.history.replaceState(null, '', path));
		window.scrollTo({ top: 0 });

		if (emitEvents) {
			window.dispatchEvent(new Event(LocationReplaceEvent));
			window.dispatchEvent(
				new CustomEvent<LocationChangeEventPayload>(LocationChangeEvent, {
					detail: { direction: LocationReplaceEvent, transition },
				})
			);
		}
	});
}

export function pop({ transition }: NavigationOptions = {}): void {
	__safeCall(() => {
		popTransition = transition;
		window.history.back();
	});
}

export function refresh(): void {
	__safeCall(() => {
		window.dispatchEvent(new Event(LocationRefreshEvent));
		window.dispatchEvent(
			new CustomEvent<LocationChangeEventPayload>(LocationChangeEvent, {
				detail: { direction: LocationRefreshEvent },
			})
		);
	});
}

export function canGoBack(): boolean {
	return currentIndex > 0;
}

export function isCurrent(currentPath: string): boolean {
	return __safeCall<boolean>(() => window.location.pathname === currentPath);
}

export function current(): string {
	return __safeCall<string>(() => window.location.pathname);
}

export function previous(): string | null {
	return previousPath;
}

type ChangeCallback = (currentPath: string, direction?: string) => void;

export function onChange(callback: ChangeCallback): void {
	callback(window.location.pathname);

	window.addEventListener(LocationChangeEvent, (event: CustomEvent) => {
		callback(window.location.pathname, event.detail.direction);
	});
}
